import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { modifier } from 'ember-modifier';
import style from 'ember-style-modifier/modifiers/style';
import {
  setupCanvas,
  drawGridLines,
  drawYAxisLabels,
  drawXAxisLabels,
  drawAreaFill,
} from '../../utils/charts/canvas-utils.ts';
import { linearScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import clampTooltip from '../../modifiers/clamp-tooltip.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

interface AreaChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Fixed height in pixels (default: 300) */
      height?: number;
      /** Fill opacity (default: 0.3) */
      fillOpacity?: number;
      /** Show point markers (default: false) */
      showPoints?: boolean;
    };
  Element: HTMLDivElement;
}

const DEFAULT_HEIGHT = 300;

export default class AreaChart extends Component<AreaChartSignature> {
  @tracked containerWidth = 400;
  @tracked containerHeight = 0;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get height() {
    if (this.args.height) return this.args.height;
    return this.containerHeight > 0 ? this.containerHeight : DEFAULT_HEIGHT;
  }

  get color() {
    return this.args.colors?.[0] ?? DEFAULT_CHART_COLORS[2]!;
  }

  get data() {
    return this.args.data ?? [];
  }

  get fillOpacity() {
    return this.args.fillOpacity ?? 0.3;
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.data[this.hoveredIndex]) return null;
    const d = this.data[this.hoveredIndex]!;
    return { category: d.category, value: formatNumber(d.value) };
  }

  private pointPositions: Array<{ x: number; y: number; index: number }> = [];

  installCanvas = modifier((element: HTMLElement) => {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      if (entry.contentRect.height > 0) {
        this.containerHeight = entry.contentRect.height;
      }
      this.draw(canvas);
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 400;
    this.containerHeight = element.clientHeight || 0;
    this.draw(canvas);

    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      this.hoveredIndex = null;
      this.draw(canvas);
    };
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  });

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.data;
    if (!data.length) return;

    const shouldRotate = false;
    const bottomMargin = 44;
    const layout = computeLayout(width, height, { bottom: bottomMargin, left: 55 });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const yScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );

    const xStep = data.length > 1 ? layout.innerWidth / (data.length - 1) : 0;
    const xPositions = data.map((_, i) => layout.left + i * xStep);

    drawGridLines(ctx, ticks, yScale, layout.left, layout.left + layout.innerWidth, theme.grid);

    // Y axis
    const formatFn = this.args.formatValue ?? formatAxisValue;
    drawYAxisLabels(ctx, ticks, yScale, layout.left - 8, theme.textMuted, formatFn);

    // X axis (with thinning for dense data)
    const labels = data.map((d) => d.category);
    const maxLabels = Math.max(4, Math.floor(layout.innerWidth / 96));
    drawXAxisLabels(
      ctx,
      labels,
      xPositions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      shouldRotate,
      maxLabels
    );

    this.pointPositions = data.map((d, i) => ({
      x: xPositions[i]!,
      y: yScale(d.value),
      index: i,
    }));

    const baseline = layout.top + layout.innerHeight;

    // Draw filled area — Peel-style gradient from color to transparent
    drawAreaFill(
      ctx,
      this.pointPositions.map((p) => ({ x: p.x, y: p.y })),
      baseline,
      this.color,
      this.fillOpacity,
      0.02
    );

    // Draw line on top
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    this.pointPositions.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();

    if (this.args.showPoints) {
      this.pointPositions.forEach((p, i) => {
        const isHovered = this.hoveredIndex === i;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, isHovered ? 5 : 3, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    if (this.hoveredIndex !== null && this.pointPositions[this.hoveredIndex]) {
      const p = this.pointPositions[this.hoveredIndex]!;
      ctx.strokeStyle = theme.grid;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(p.x, layout.top);
      ctx.lineTo(p.x, baseline);
      ctx.stroke();
      ctx.setLineDash([]);

      // Highlight point
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = theme.tooltipBg;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Axis titles
    if (this.args.xLabel) {
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, height - 4);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.translate(14, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest: { dist: number; index: number } | null = null;
    for (const p of this.pointPositions) {
      const dist = Math.hypot(mx - p.x, my - p.y);
      if (dist < 30 && (!closest || dist < closest.dist)) {
        closest = { dist, index: p.index };
      }
    }

    if (closest) {
      if (this.hoveredIndex !== closest.index) {
        this.hoveredIndex = closest.index;
        const p = this.pointPositions[closest.index]!;
        this.tooltipX = rect.left + p.x;
        this.tooltipY = rect.top + p.y - 8;
        this.draw(canvas);
      }
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest: { dist: number; index: number } | null = null;
    for (const p of this.pointPositions) {
      const dist = Math.hypot(mx - p.x, my - p.y);
      if (dist < 30 && (!closest || dist < closest.dist)) {
        closest = { dist, index: p.index };
      }
    }

    if (closest && this.args.onClick && this.data[closest.index]) {
      this.args.onClick(this.data[closest.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="area"
      class="relative w-full h-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas
        {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
      ></canvas>

      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="tio-chart-tooltip -translate-x-1/2 -translate-y-full"
          {{style left=(concat this.tooltipX "px") top=(concat this.tooltipY "px")}}
          {{clampTooltip (concat this.tooltipX ":" this.tooltipY)}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="tio-text-muted">{{this.tooltipData.value}}</div>
        </div>
      {{/if}}
    </div>
  </template>
}

export type { AreaChartSignature };
